import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Tenant, TenantInput } from '#/graphql/types';
import Form, { FormAction, FormRefType } from '#/common/components/Form';
import fields from './fields';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import useTenantUpdate from '../../hooks/useUpdateTenantMutation';
import { get } from 'lodash';
import useTenantCreate from '../../hooks/useCreateTenantMutation';
import useTenantDelete from '../../hooks/useDeleteTenantMutation';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import useSession from '#/common/hooks/useSession';
import useWorkOrderCreate from '#/modules/workOrder/hooks/useWorkOrderCreate';
import { UserRole } from '#/common/types/userTypes';
import syncTisTenantDataMutation from '../../hooks/useSyncTisTenantDataMutation';
import type { FieldDataChangedEvent } from 'devextreme/ui/form';
import type { EventInfo } from 'devextreme/events';
import type dxForm from 'devextreme/ui/form';
import { setItemOptions, setEditorOptions } from '#/common/components/Form/formItemOption';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import { styled } from '@mui/system';
import { color } from '@nuvolo/nuux-theme/utils';

const ModalTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const IconHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
  borderRadius: '50%',
  backgroundColor: color('custom', 'defaultIconBackground'),

  '& > svg': {
    fontSize: '24px',
    color: color('warning', 'main'),
  },
});

const WarningContent = styled('div')({
  padding: '16px 0',
  fontSize: '14px',
  lineHeight: '1.5',
  color: color('custom', 'constantGray'),
});

const FormWrapper = styled('div')({
  '& .data-source-divider': {
    borderTop: `1px solid ${color('primary', 'light3')}`,
    paddingTop: '20px',
    marginTop: '10px',
  },
});

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: true,
  [UserRole.trane_admin]: true,
  [UserRole.technician]: true,
};

const actionsPerUserRole: Record<UserRole, ('fetchTISData' | 'alarm' | 'criticalAlarm')[]> = {
  [UserRole.asset_manager]: ['fetchTISData', 'alarm', 'criticalAlarm'],
  [UserRole.trane_admin]: ['fetchTISData', 'alarm', 'criticalAlarm'],
  [UserRole.technician]: [],
};

export interface TenantFormProps {
  tenant: Tenant | {};
  title: string;
}

const TenantForm: React.FC<TenantFormProps> = ({ tenant, title }) => {
  const { alert, confirm } = useToast();

  const { user } = useSession();
  const role = user?.role as UserRole | undefined;
  const isSuperAdmin = role === UserRole.trane_admin;
  // TO BE CHANGED ONCE NEW ROLE IS ACTIVATED

  const navigate = useNavigate();
  const id = get(tenant, 'id', undefined);
  const formRef = useRef<FormRefType | null>(null);
  const formInstanceRef = useRef<dxForm | null>(null);

  // Get the actual dataSource from tenant (could be loaded async)
  const tenantDataSource = get(tenant, 'dataSource', null) as string | null;

  // Normalize tenant data with default values for dataSource
  const normalizedTenant = useMemo(() => {
    if (!tenant || !('id' in tenant)) return tenant;
    return {
      ...tenant,
      dataSource: tenant.dataSource || 'tis',
    };
  }, [tenant]);

  // Use refs for reliable tracking of data source changes
  const confirmedDataSourceRef = useRef<string>(tenantDataSource || 'tis');
  const suppressChangeEventRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);

  // Update refs when tenant data loads/changes (async load scenario)
  // Only update on initial load, not after user has made changes
  useEffect(() => {
    if (tenantDataSource !== null && !hasInitializedRef.current) {
      confirmedDataSourceRef.current = tenantDataSource;
      hasInitializedRef.current = true;
    }
  }, [tenantDataSource]);

  // Update dataSourceId visibility when tenant data loads asynchronously
  // Use confirmedDataSourceRef to respect the user's current selection
  useEffect(() => {
    if (formInstanceRef.current && isSuperAdmin) {
      const currentDataSource = confirmedDataSourceRef.current;
      const showDataSourceId = currentDataSource !== 'tis';
      setItemOptions(formInstanceRef.current, 'dataSourceId', 'visible', showDataSourceId);
    }
  }, [tenantDataSource, isSuperAdmin]);

  // State for data source change confirmation modal
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [pendingDataSource, setPendingDataSource] = useState<string | null>(null);

  // Defined in the modules hooks dir
  const { createWorkOrder } = useWorkOrderCreate();
  const { updateTenant } = useTenantUpdate();
  const { createTenant } = useTenantCreate();
  const { deleteTenant } = useTenantDelete();
  const { syncTisTenant } = syncTisTenantDataMutation();

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteTenant(id);
    },
    [id, deleteTenant],
  );

  // Submit that updates or creates a new record based on if there is ID present
  const submit = useCallback(
    (data: TenantInput) => {
      if (!id) {
        return createTenant(data);
      }
      return updateTenant(id, data);
    },
    [id, updateTenant, createTenant],
  );

  const isFormReadonly = !role || formReadonlyRole[role];

  // Set initial visibility and read-only states based on role and dataSource
  const onContentReady = useCallback(
    (e: EventInfo<dxForm>) => {
      const form = e.component;
      if (!form) return;

      // Store form instance ref for modal handlers
      formInstanceRef.current = form;

      const currentDataSource = get(normalizedTenant, 'dataSource', 'tis') as string;
      const showDataSourceId = currentDataSource !== 'tis' && isSuperAdmin;

      // Set dataSourceId visibility based on dataSource and role
      setItemOptions(form, 'dataSourceId', 'visible', showDataSourceId);

      // For existing tenants: make all fields readonly except dataSource and dataSourceId (for everyone)
      if (id) {
        const readonlyFields = [
          'name',
          'address',
          'contacts',
          'isActive',
          'cognitoGroupName',
          'tisId',
          'plan',
        ];
        readonlyFields.forEach((field) => {
          setEditorOptions(form, field, { readOnly: true });
        });
      }

      // For non-super admins: also make dataSource read-only and always hide dataSourceId
      if (!isSuperAdmin) {
        setEditorOptions(form, 'dataSource', { readOnly: true });
        setItemOptions(form, 'dataSourceId', 'visible', false);
      }
    },
    [normalizedTenant, isSuperAdmin, id],
  );

  // Handle dataSource field changes with confirmation modal
  const onFieldDataChanged = useCallback(
    (e: FieldDataChangedEvent) => {
      const { component: form, dataField: field, value } = e;

      if (field === 'dataSource' && isSuperAdmin) {
        // Skip if this is a programmatic revert
        if (suppressChangeEventRef.current) {
          return;
        }

        const newDataSource = value as string;
        const currentConfirmedValue = confirmedDataSourceRef.current;

        // Only show confirmation if changing to a different value and it's an existing tenant
        if (id && newDataSource !== currentConfirmedValue) {
          setPendingDataSource(newDataSource);
          setShowDataSourceModal(true);

          // Revert to the confirmed value while modal is shown
          suppressChangeEventRef.current = true;
          form.updateData('dataSource', currentConfirmedValue);
          suppressChangeEventRef.current = false;
          return;
        }

        // Update visibility for new tenants or same value
        const showDataSourceId = newDataSource !== 'tis';
        setItemOptions(form, 'dataSourceId', 'visible', showDataSourceId);
        // Clear dataSourceId when switching to TIS
        if (newDataSource === 'tis') {
          form.updateData('dataSourceId', null);
        }
        confirmedDataSourceRef.current = newDataSource;
      }
    },
    [id, isSuperAdmin],
  );

  // Handle modal confirmation - apply the pending data source change
  const handleDataSourceConfirm = useCallback(() => {
    if (pendingDataSource && formInstanceRef.current) {
      const form = formInstanceRef.current;
      const showDataSourceId = pendingDataSource !== 'tis';

      // Apply the pending change (suppress event to avoid recursive modal)
      suppressChangeEventRef.current = true;
      form.updateData('dataSource', pendingDataSource);
      suppressChangeEventRef.current = false;

      setItemOptions(form, 'dataSourceId', 'visible', showDataSourceId);
      // Clear dataSourceId when switching to TIS
      if (pendingDataSource === 'tis') {
        form.updateData('dataSourceId', null);
      }
      confirmedDataSourceRef.current = pendingDataSource;
    }

    // Close modal and clear pending state
    setShowDataSourceModal(false);
    setPendingDataSource(null);
  }, [pendingDataSource]);

  // Handle modal cancellation - keep the previous value
  const handleDataSourceCancel = useCallback(() => {
    setShowDataSourceModal(false);
    setPendingDataSource(null);
  }, []);

  const actions = useMemo(() => {
    const result: FormAction<Tenant>[] = [];

    if (!isFormReadonly && deleteItem && id) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          const sure = await confirm(
            'error',
            `Are you sure you want to delete the tenant: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          try {
            if (!e.record?.id) {
              return;
            }

            e.setIsLoading(true);
            const result = await deleteItem(e.record.id);
            e.setIsLoading(false);
            if (result?.errors) {
              void alert('error', result.errors.join(`;\\n`));
              return;
            }
            navigate('/admin/tenants');
          } catch (e: any) {
            console.log('Error', e);
            void alert('error', "Couldn't delete tenant!");
          }
        },
      });
    }
    if (id) {
      if (role && actionsPerUserRole[role].includes('fetchTISData'))
        result.push({
          label: 'Fetch data',
          type: 'normal',
          onClick: async (e) => {
            const sure = await confirm(
              'warning',
              `Are you sure you want to fetch the TIS data for tenant "${e.record?.name}"?`,
            );

            if (!sure) {
              return;
            }

            try {
              e.setIsLoading(true);
              const result = await syncTisTenant([id]);
              e.setIsLoading(false);

              if (result?.errors) {
                throw result.errors;
              }

              void alert(
                'success',
                `Buildings, Assets and Asset properties synced! Findings and Alerts are scheduled for sync and will be done in a few minutes.`,
              );
            } catch (e: any) {
              console.log('TIS Sync error', e);
              void alert('error', 'Opps, something unexpected happened!');
            }
          },
        });

      // Save button for Super Admin to save data source changes
      if (isSuperAdmin && id) {
        result.push({
          label: 'Save',
          type: 'normal',
          onClick: async (e) => {
            if (!e.record) {
              return;
            }

            try {
              e.setIsLoading(true);
              // Include required name field along with dataSource fields for Super Admin updates
              const dataSource = e.record.dataSource || 'tis';
              const dataSourceUpdate: Record<string, unknown> = {
                name: e.record.name,
                dataSource,
                // Explicitly set dataSourceId: include value for FSM, set null for TIS
                dataSourceId: dataSource === 'tis' ? null : e.record.dataSourceId || null,
              };
              const result = await updateTenant(id, dataSourceUpdate as unknown as TenantInput);
              e.setIsLoading(false);

              if (result?.errors) {
                void alert('error', result.errors.join(';\n'));
                return;
              }

              confirmedDataSourceRef.current = dataSource;

              // Update dataSourceId visibility after save
              if (formInstanceRef.current) {
                const showDataSourceId = dataSource !== 'tis';
                setItemOptions(
                  formInstanceRef.current,
                  'dataSourceId',
                  'visible',
                  showDataSourceId,
                );
              }

              void alert('success', 'Data source changes saved!');
              e.clearDirty();
            } catch (err: any) {
              e.setIsLoading(false);
              console.log('Error', err);
              void alert('error', 'Missing or invalid data!');
            }
          },
        });
      }
    }
    if (!isFormReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          let sure = true;
          const isGroupNameChanged = get(e, 'record.contacts') != get(tenant, 'contacts');
          if (isGroupNameChanged)
            sure = await confirm(
              'warning',
              `Changing the "Gognito Group Name" might cause problems with user access to this tenant, are you sure you want to proceed?`,
            );

          if (!e.record || !sure) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\\n`));
              return;
            }
            if (!id && result?.data && result?.data.id) {
              navigate(`/tenants/${result.data?.id}`);
            }

            void alert('success', 'Changes saved!');
            e.clearDirty();
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, tenant, deleteItem, user, isSuperAdmin, updateTenant, id]);
  return (
    <>
      <FormWrapper>
        <Form
          ref={formRef}
          record={normalizedTenant as Tenant}
          fields={fields}
          actions={actions}
          onContentReady={onContentReady}
          onFieldDataChanged={onFieldDataChanged}
        />
      </FormWrapper>

      {/* Data Source Change Confirmation Modal */}
      <Modal
        visible={showDataSourceModal}
        title={
          <ModalTitle>
            <span>Change of Data Source</span>
          </ModalTitle>
        }
        dragEnabled
        width={500}
        height="auto"
        onHiding={handleDataSourceCancel}
        content={
          <WarningContent>
            Changing the Data Source will affect all current Asset Types, Models, Locations and
            Assets. All of the data will be lost and new data will be fetched from the selected data
            source within the next 24 hours.
          </WarningContent>
        }
        actions={[
          <TMMButton
            className="width-50"
            key="cancel"
            label="Back to Edit"
            type="normal"
            onClick={handleDataSourceCancel}
          />,
          <TMMButton
            className="width-50"
            key="confirm"
            label="Confirm New Data Source"
            type="submit"
            onClick={handleDataSourceConfirm}
          />,
        ]}
      />
    </>
  );
};

export default React.memo(TenantForm);
